/*
 * Vitelyn motion engine.
 *
 * Two jobs, no dependencies:
 *
 * 1. Reveals. [data-reveal], [data-clip] and [data-lines] get their "in" state
 *    once, when they first enter the viewport. Content is visible by default:
 *    the hidden starting state only exists under html.mx, which the inline
 *    head script sets when motion is allowed, and which it removes again if
 *    this file has not started within 2.5s. A blocked script can never blank
 *    the page.
 *
 * 2. Scenes. A [data-scene] element is a scroll track. Its progress (0..1) is
 *    computed from cached offsets in a single requestAnimationFrame per scroll
 *    event, and only for scenes that are near the viewport. Each scene is a
 *    pure function of that number and writes transform/opacity (or a couple
 *    of attributes) directly. Modes:
 *      pin     - the track is taller than the screen and holds a sticky stage;
 *                0 when the track's top meets the viewport top, 1 when its
 *                bottom meets the viewport bottom.
 *      leave   - 0 at the track's top, 1 once its bottom has scrolled past
 *                the top of the viewport (the hero hand-off).
 *      through - 0 as the track enters from below, 1 as it leaves at the top.
 *
 * Why not GSAP/ScrollTrigger or Lenis: every scene here is a function of one
 * scroll value, which sticky positioning plus this loop already provides for
 * ~6 KB. Lenis would replace native scrolling, which fights iOS momentum and
 * the collapsing URL bar.
 */
(function () {
  'use strict';

  window.vxReveal = true;

  var doc = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var canObserve = 'IntersectionObserver' in window;

  function motionOK() {
    return !reduced.matches && doc.classList.contains('mx') && canObserve;
  }

  function clamp(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
  function range(a, b, v) { return clamp((v - a) / (b - a)); }
  function lerp(a, b, t) { return a + (b - a) * t; }
  // easeInOutCubic: scrubbed values should start and end gently.
  function ease(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }

  /* ------------------------------------------------------------------ */
  /* Reveals                                                             */
  /* ------------------------------------------------------------------ */

  var REVEALS = [
    ['[data-reveal]', function (el) { el.setAttribute('data-reveal', 'in'); }],
    ['[data-clip]', function (el) { el.setAttribute('data-clip', 'in'); }],
    ['[data-lines]', function (el) { el.classList.add('is-in'); }]
  ];

  function arm(el, show) {
    if (el.__vxArmed) return;
    el.__vxArmed = true;
    if (!motionOK()) { show(el); return; }
    // A clipped element reports zero intersection, so watch its parent.
    var host = el.hasAttribute('data-clip') ? el.parentElement || el : el;
    var io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) { show(el); io.disconnect(); }
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.01 });
    io.observe(host);
  }

  function scanReveals(root) {
    REVEALS.forEach(function (r) {
      (root || document).querySelectorAll(r[0]).forEach(function (el) { arm(el, r[1]); });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Scenes                                                              */
  /* ------------------------------------------------------------------ */

  var SCENES = {
    // The hero hands off to the formula: as it leaves, the bottle rises a
    // little, turns to show its dose panel, and the copy steps back.
    hero: function (el) {
      var bottle = el.querySelector('[data-hero-bottle]');
      var copy = el.querySelector('[data-hero-copy]');
      return function (p) {
        var e = ease(p);
        // The whole bottle moves as one object: it lifts, grows a little and
        // leans back as the hero hands over to the next scene.
        if (bottle) bottle.style.transform =
          'translate3d(0,' + (-e * 14).toFixed(2) + '%,0) scale(' + (1 + e * 0.08).toFixed(4) + ') rotate(' + (-e * 5).toFixed(2) + 'deg)';
        if (copy) {
          copy.style.opacity = (1 - range(0, 0.55, p)).toFixed(3);
          copy.style.transform = 'translate3d(0,' + (-p * 64).toFixed(1) + 'px,0)';
        }
      };
    },

    // 47 tabs close to one. Discrete: only touches the DOM when the count
    // changes; the strip's CSS transitions do the easing.
    tabs: function (el) {
      var tabs = Array.prototype.slice.call(el.querySelectorAll('.lx-tab'));
      var total = tabs.length;
      var count = el.querySelector('[data-count]');
      var noun = el.querySelector('[data-noun]');
      var lastN = -1, lastPhase = '';
      return function (p) {
        var n = Math.max(1, Math.round(total - (total - 1) * ease(range(0.1, 0.72, p))));
        if (n !== lastN) {
          for (var i = 0; i < total; i++) {
            tabs[i].classList.toggle('is-closed', i >= n);
            tabs[i].classList.toggle('is-active', i === n - 1);
          }
          if (count) count.textContent = n;
          if (noun) noun.textContent = n === 1 ? noun.dataset.one : noun.dataset.many;
          lastN = n;
        }
        var phase = p > 0.78 ? 'one' : 'open';
        if (phase !== lastPhase) { el.setAttribute('data-phase', phase); lastPhase = phase; }
      };
    },

    // The formula: the bottle turns towards its dose panel while the five
    // actives leave it one at a time.
    formula: function (el) {
      var items = Array.prototype.slice.call(el.querySelectorAll('[data-ing]'));
      var ticks = Array.prototype.slice.call(el.querySelectorAll('[data-tick]'));
      var bottle = el.querySelector('.lx-formula__bottle');
      var N = items.length;
      var START = 0.08, SPAN = 0.74;
      var lastIdx = -2, lastPhase = '';
      return function (p) {
        var t = ease(range(0, 0.92, p));
        // The bottle stays facing front and drifts gently while the actives leave it.
        if (bottle) bottle.style.transform = 'translate3d(0,' + lerp(3, -3, t).toFixed(2) + '%,0) scale(' + lerp(1, 0.95, t).toFixed(4) + ')';

        var idx = p < START ? -1 : Math.min(N, Math.floor((p - START) / (SPAN / N)));
        if (idx !== lastIdx) {
          for (var i = 0; i < N; i++) {
            items[i].setAttribute('data-state', i < idx ? 'after' : i === idx ? 'active' : 'before');
            if (ticks[i]) ticks[i].classList.toggle('is-on', i <= idx);
          }
          lastIdx = idx;
        }
        var phase = idx >= N ? 'done' : 'play';
        if (phase !== lastPhase) { el.setAttribute('data-phase', phase); lastPhase = phase; }
      };
    },

    // The dropper fills to the 1 mL line while the steps pass.
    ritual: function (el) {
      var dropper = el.querySelector('.lx-dropper');
      var steps = Array.prototype.slice.call(el.querySelectorAll('[data-step]'));
      var last = -1, lastFull = null;
      return function (p) {
        var fill = ease(range(0.28, 0.6, p));
        if (dropper) dropper.style.setProperty('--fill', fill.toFixed(3));
        var cur = p < 0.3 ? 0 : fill < 0.995 ? 1 : 2;
        cur = Math.min(cur, steps.length - 1);
        if (cur !== last) {
          steps.forEach(function (s, i) { s.toggleAttribute('data-current', i === cur); });
          last = cur;
        }
        var full = fill >= 0.995;
        if (full !== lastFull) { el.toggleAttribute('data-full', full); lastFull = full; }
      };
    }
  };

  var scenes = [];
  var ticking = false;
  var vh = window.innerHeight;

  function measure() {
    vh = window.innerHeight;
    var y = window.scrollY || window.pageYOffset;
    scenes.forEach(function (s) {
      var r = s.el.getBoundingClientRect();
      s.top = r.top + y;
      s.height = s.el.offsetHeight;
      s.stageH = s.stage ? s.stage.offsetHeight : vh;
    });
  }

  function progress(s, y) {
    if (s.mode === 'pin') return clamp((y - s.top) / Math.max(1, s.height - s.stageH));
    if (s.mode === 'leave') return clamp((y - s.top) / Math.max(1, s.height));
    return clamp((y + vh - s.top) / Math.max(1, s.height + vh));
  }

  // Scroll input is coarse: a mouse wheel moves ~100px per notch, so a scene
  // mapped straight onto scrollY advances in visible steps. Each scene instead
  // eases its own progress towards the scroll position (exponential smoothing,
  // corrected for frame time so 60Hz and 120Hz feel the same). Native
  // scrolling is untouched; only what the scenes draw trails it slightly.
  var SMOOTH = 0.14;          // share of the remaining distance covered per 60Hz frame
  var lastT = 0;

  function frame(now) {
    ticking = false;
    var dt = lastT ? Math.min(64, now - lastT) : 16.7;
    lastT = now;
    var k = 1 - Math.pow(1 - SMOOTH, dt / 16.7);
    var y = window.scrollY || window.pageYOffset;
    var moving = false;
    for (var i = 0; i < scenes.length; i++) {
      var s = scenes[i];
      if (!s.active) continue;
      var target = progress(s, y);
      // First frame, or a jump (anchor link, resize): land instead of gliding.
      if (s.cur < 0 || Math.abs(target - s.cur) > 0.35) s.cur = target;
      else s.cur += (target - s.cur) * k;
      if (Math.abs(target - s.cur) < 0.0005) s.cur = target;
      else moving = true;
      if (Math.abs(s.cur - s.last) > 0.0002) { s.last = s.cur; s.update(s.cur); }
    }
    if (moving) request();
    else lastT = 0;
  }

  function request() {
    if (!ticking) { ticking = true; window.requestAnimationFrame(frame); }
  }

  function mountScenes(root) {
    if (!motionOK()) return;
    (root || document).querySelectorAll('[data-scene]').forEach(function (el) {
      if (el.__vxScene) return;
      var make = SCENES[el.getAttribute('data-scene')];
      if (!make) return;
      var s = {
        el: el,
        mode: el.getAttribute('data-scene-mode') || 'through',
        stage: el.querySelector('.lx-stage'),
        update: make(el),
        active: false,
        last: -1,
        cur: -1
      };
      el.__vxScene = s;
      el.classList.add('is-live');
      scenes.push(s);
      new IntersectionObserver(function (entries) {
        s.active = entries[0].isIntersecting;
        if (s.active) request();
      }, { rootMargin: '25% 0px 25% 0px' }).observe(el);
    });
    measure();
    request();
  }

  function boot() {
    scanReveals();
    mountScenes();
    window.addEventListener('scroll', request, { passive: true });
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { measure(); request(); }, 120);
    });
    // Late layout shifts (fonts, a FAQ row opening, app blocks) move the tracks.
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () { measure(); request(); });
      ro.observe(document.body);
    }
  }

  // A user switching reduced motion on mid-visit gets the still layout.
  reduced.addEventListener && reduced.addEventListener('change', function (e) {
    if (e.matches) doc.classList.remove('mx');
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // The theme editor swaps section markup in without a page load.
  document.addEventListener('shopify:section:load', function (e) {
    scanReveals(e.target);
    mountScenes(e.target);
  });
})();
